
import React from "react";
import { Input } from "@/components/ui/input";
import { ColumnKey } from "../types/workout-types";

interface WorkoutCellProps {
  isActive: boolean;
  editMode: boolean;
  cellValue: string;
  tempValue: string;
  rowIndex: number;
  colIndex: number;
  cellId: string;
  column: ColumnKey;
  onEdit: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => void;
  onBlur: () => void;
  onFocus: () => void;
  onClick: () => void;
  onDoubleClick: () => void;
  onClickOutside: () => void;  
  setRef: (el: HTMLInputElement | null) => void;
}

function WorkoutCellComponent({
  isActive,
  editMode,
  cellValue,
  tempValue,
  rowIndex,
  colIndex,
  cellId,
  column,
  onEdit,
  onKeyDown,
  onBlur,
  onFocus,
  onClick,
  onDoubleClick,
  onClickOutside,
  setRef,
}: WorkoutCellProps) {
  // Determine text alignment based on column type
  const getAlignment = (col: ColumnKey) => {
    if (col === "exercise" || col === "notes") {
      return "text-left";
    }
    return "text-center";
  };
  
  // Create a ref to track activation changes
  const wasActiveRef = React.useRef(isActive);
  
  // Maintain a class name state for smooth transitions
  const [bgClass, setBgClass] = React.useState(
    isActive ? "bg-white/15" : "bg-transparent"
  );
  
  // Smoothly transition cell background
  React.useEffect(() => {
    // Only apply transition when there's a real state change
    if (wasActiveRef.current !== isActive) {
      wasActiveRef.current = isActive;
      
      if (isActive) {
        // Immediately activate for a responsive feel
        setBgClass("bg-white/15");
      } else {
        // Smoothly transition when becoming inactive
        setBgClass("bg-transparent");
      }
    }
  }, [isActive]);

  // Handle clicks outside the cell
  const handleClickOutside = React.useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const cell = document.getElementById(cellId);
    
    if (cell && !cell.contains(target)) {
      onClickOutside();
    }
  }, [cellId, onClickOutside]);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <td
      id={cellId}
      className={`border border-white/10 rounded-sm p-0 transition-colors duration-300 ${
        isActive ? "bg-white/15" : "hover:bg-white/5"
      }`}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <Input
        id={`input-${cellId}`}
        ref={setRef}
        value={isActive && editMode ? tempValue : cellValue}
        onChange={(e) => onEdit(e.target.value)}
        onKeyDown={(e) => onKeyDown(e, rowIndex, colIndex)}
        onBlur={onBlur}
        onFocus={onFocus}
        className={`border-0 bg-transparent px-2 py-1 h-9 w-full focus-visible:ring-1 focus-visible:ring-white/30 focus-visible:ring-inset rounded-sm text-white transition-colors duration-300 ${getAlignment(column)} ${bgClass}`}
        readOnly={!(isActive && editMode)}
        placeholder={`Enter ${column}`}
      />
    </td>
  );
}

// Implement memoization with custom comparison function
export const WorkoutCell = React.memo(WorkoutCellComponent, (prevProps, nextProps) => {
  // Check only the props that would cause visual changes
  return (
    prevProps.isActive === nextProps.isActive &&
    prevProps.editMode === nextProps.editMode &&
    prevProps.cellValue === nextProps.cellValue &&
    (prevProps.isActive && prevProps.editMode ? prevProps.tempValue === nextProps.tempValue : true)
  );
});
